// ----------------------------------------------------------------------

export const stylesMode = {
  light: '[data-mui-color-scheme="light"] &',
  dark: '[data-mui-color-scheme="dark"] &',
};

export const mediaQueries = {
  upXs: '@media (min-width:0px)',
  upSm: '@media (min-width:600px)',
  upMd: '@media (min-width:900px)',
  upLg: '@media (min-width:1200px)',
  upXl: '@media (min-width:1536px)',
};

/**
 * Set font family
 */
export const setFont = (fontName: string) =>
  `"${fontName}",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"`;

/**
 * Converts rem to px
 */
export const remToPx = (value: string): number => Math.round(parseFloat(value) * 16);

/**
 * Converts px to rem
 */
export const pxToRem = (value: number): string => `${value / 16}rem`;

/**
 * Responsive font sizes
 */
export const responsiveFontSizes = ({ sm, md, lg }: { sm: number; md: number; lg: number }) => ({
  [mediaQueries.upSm]: { fontSize: pxToRem(sm) },
  [mediaQueries.upMd]: { fontSize: pxToRem(md) },
  [mediaQueries.upLg]: { fontSize: pxToRem(lg) },
});

/**
 * Converts a hex color to RGB channels
 */
export const hexToRgbChannel = (hex: string) => {
  if (!/^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(hex)) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  let r = 0;
  let g = 0;
  let b = 0;

  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }

  return `${r} ${g} ${b}`;
};

/**
 * Converts a hex color to RGB channels
 */
export const createPaletteChannel = (hexPalette: Record<string, string>) => {
  const channelPalette: Record<string, string> = {};

  Object.entries(hexPalette).forEach(([key, value]) => {
    channelPalette[`${key}Channel`] = hexToRgbChannel(value);
  });

  return { ...hexPalette, ...channelPalette };
};

/**
 * Color with alpha channel
 */
export const varAlpha = (color: string, opacity = 1) => {
  const colorValue = color.startsWith('#') ? hexToRgbChannel(color) : color;

  const unsupported =
    colorValue.startsWith('rgb') ||
    colorValue.startsWith('rgba') ||
    (!colorValue.includes('var') && colorValue.includes('Channel'));

  if (unsupported) {
    throw new Error(
      `[Alpha]: Unsupported color format "${color}".
       Supported formats are:
       - RGB channels: "0 184 217".
       - Hex: "#00B8D9".
       - CSS variables with "Channel" prefix: "var(--palette-common-blackChannel, #000000)".
       Unsupported formats are:
       - RGB: "rgb(0, 184, 217)".
       - RGBA: "rgba(0, 184, 217, 1)".
       `
    );
  }

  return `rgba(${colorValue} / ${opacity})`;
};
